import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";

import { UrlConstantes } from "../utilerias/constantes/urlconstantes";
import {
  Matriz,
  MatrizActivo,
  MatrizEvidenciaRes,
  MatrizPendienteReq,
  MatrizPendienteRes,
  MatrizRes,
  MatrizRespaldoReq,
  MatrizRespaldoRes,
  MatrizRiesgoReq,
  MatrizRiesgoRes,
  MatrizSeguimiento,
  MatrizVersion,
  RiesgoProceso,
  RiesgoProcesoReq,
  RiesgoProcesoRes,
  RiesgoProcesoSeguimiento,
  ImportacionRes,
} from "../shared/model/iso42001/matriz-riesgo.model";

/**
 * Servicio de la Matriz de Riesgos IA.
 * Todo va por POST, incluso las lecturas, igual que el resto del proyecto.
 * El manejo de errores lo hace cada componente en su subscribe.
 */
@Injectable({ providedIn: "root" })
export class MatrizRiesgoService {
  constructor(private http: HttpClient) {}

  /**
   * Los <input type="date"> entregan "yyyy-MM-dd" y Jackson interpreta esa
   * cadena como medianoche UTC: en el servidor (America/Mexico_City) eso son
   * las 18:00 del día ANTERIOR, y la columna DATE guardaba un día menos del
   * capturado. Aquí se convierte a un instante al mediodía local, que cae en
   * el mismo día de este lado y del otro.
   *
   * Solo toca claves que empiezan con "fecha": un texto libre que casualmente
   * fuera una fecha no debe transformarse.
   */
  private static fechasAlDia(valor: any, llave?: string): any {
    const soloDia = /^\d{4}-\d{2}-\d{2}$/;
    if (
      typeof valor === "string" &&
      llave &&
      llave.indexOf("fecha") === 0 &&
      soloDia.test(valor)
    ) {
      const [a, m, d] = valor.split("-").map(Number);
      return new Date(a, m - 1, d, 12, 0, 0).toISOString();
    }
    if (Array.isArray(valor)) {
      return valor.map((x) => MatrizRiesgoService.fechasAlDia(x));
    }
    if (valor && typeof valor === "object" && !(valor instanceof Date)) {
      const copia: any = {};
      Object.keys(valor).forEach((k) => {
        copia[k] = MatrizRiesgoService.fechasAlDia(valor[k], k);
      });
      return copia;
    }
    return valor;
  }

  private static conFechas<T>(req: T): T {
    return MatrizRiesgoService.fechasAlDia(req) as T;
  }

  consultar(filtro?: MatrizActivo): Observable<MatrizRiesgoRes> {
    const req: MatrizRiesgoReq = { filtro };
    return this.http.post<MatrizRiesgoRes>(UrlConstantes.ISO42001_MATRIZ_CONSULTAR, req);
  }

  /**
   * El listado con el árbol de cada activo, en una sola llamada. Es lo que usa
   * la pantalla de tarjetas: antes pedía el detalle activo por activo y con
   * varias decenas la primera carga se notaba.
   */
  consultarCompleto(filtro?: MatrizActivo): Observable<MatrizRiesgoRes> {
    const req: MatrizRiesgoReq = { filtro };
    return this.http.post<MatrizRiesgoRes>(UrlConstantes.ISO42001_MATRIZ_CONSULTAR_COMPLETO, req);
  }

  obtener(idMatrizActivo: number): Observable<MatrizRiesgoRes> {
    const req: MatrizRiesgoReq = { idMatrizActivo };
    return this.http.post<MatrizRiesgoRes>(UrlConstantes.ISO42001_MATRIZ_OBTENER, req);
  }

  guardar(activo: MatrizActivo): Observable<MatrizRiesgoRes> {
    const req: MatrizRiesgoReq = { activo };
    return this.http.post<MatrizRiesgoRes>(UrlConstantes.ISO42001_MATRIZ_GUARDAR, MatrizRiesgoService.conFechas(req));
  }

  actualizar(activo: MatrizActivo): Observable<MatrizRiesgoRes> {
    const req: MatrizRiesgoReq = { activo };
    return this.http.post<MatrizRiesgoRes>(UrlConstantes.ISO42001_MATRIZ_ACTUALIZAR, MatrizRiesgoService.conFechas(req));
  }

  cambiarEstatus(idMatrizActivo: number, estatus: string): Observable<MatrizRiesgoRes> {
    const req: MatrizRiesgoReq = { idMatrizActivo, estatus };
    return this.http.post<MatrizRiesgoRes>(UrlConstantes.ISO42001_MATRIZ_ESTATUS, req);
  }

  eliminar(idMatrizActivo: number): Observable<MatrizRiesgoRes> {
    const req: MatrizRiesgoReq = { idMatrizActivo };
    return this.http.post<MatrizRiesgoRes>(UrlConstantes.ISO42001_MATRIZ_ELIMINAR, req);
  }

  historial(idMatrizActivo: number): Observable<MatrizRiesgoRes> {
    const req: MatrizRiesgoReq = { idMatrizActivo };
    return this.http.post<MatrizRiesgoRes>(UrlConstantes.ISO42001_MATRIZ_HISTORIAL, req);
  }

  /* ---------- Copias de seguridad ---------- */

  /**
   * Las versiones guardadas de un activo o de un riesgo, de la más nueva a la
   * más vieja.
   *
   * Sin conFechas a propósito: la petición sólo lleva identificadores, y esa
   * función reescribe cualquier clave que empiece con "fecha".
   */
  respaldos(entidad: "ACTIVO" | "RIESGO", idRegistro: number): Observable<MatrizRespaldoRes> {
    const req: MatrizRespaldoReq = { entidad, idRegistro };
    return this.http.post<MatrizRespaldoRes>(UrlConstantes.ISO42001_RESP_CONSULTAR, req);
  }

  /** La papelera: lo eliminado que todavía se puede resucitar. */
  respaldosEliminados(
    entidad?: "ACTIVO" | "RIESGO",
    idMatriz?: number,
    limite?: number
  ): Observable<MatrizRespaldoRes> {
    const req: MatrizRespaldoReq = { entidad, idMatriz, limite };
    return this.http.post<MatrizRespaldoRes>(UrlConstantes.ISO42001_RESP_ELIMINADOS, req);
  }

  /** Regresa el registro a esa copia. Devuelve en 'detalle' lo que hizo. */
  restaurarRespaldo(idRespaldo: number): Observable<MatrizRespaldoRes> {
    const req: MatrizRespaldoReq = { idRespaldo };
    return this.http.post<MatrizRespaldoRes>(UrlConstantes.ISO42001_RESP_RESTAURAR, req);
  }

  /**
   * Esa copia enfrentada contra el registro de hoy, campo por campo.
   *
   * La comparación llega hecha del servidor: ahí están los nombres en español
   * y los catálogos para traducir los niveles. Aquí sólo se pinta.
   */
  compararRespaldo(idRespaldo: number): Observable<MatrizRespaldoRes> {
    const req: MatrizRespaldoReq = { idRespaldo };
    return this.http.post<MatrizRespaldoRes>(UrlConstantes.ISO42001_RESP_COMPARAR, req);
  }

  /** Bitácora de un riesgo de proceso: quién cambió qué y cuándo. */
  historialRiesgo(idRiesgoProceso: number): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { idRiesgoProceso };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_RIESGOPROC_HISTORIAL, req);
  }

  guardarSeguimiento(seguimiento: MatrizSeguimiento): Observable<MatrizRiesgoRes> {
    const req: MatrizRiesgoReq = { seguimiento };
    return this.http.post<MatrizRiesgoRes>(UrlConstantes.ISO42001_MATRIZ_SEG_GUARDAR, MatrizRiesgoService.conFechas(req));
  }

  eliminarSeguimiento(idMatrizSeguimiento: number): Observable<MatrizRiesgoRes> {
    const req: MatrizRiesgoReq = { idMatrizSeguimiento };
    return this.http.post<MatrizRiesgoRes>(UrlConstantes.ISO42001_MATRIZ_SEG_ELIMINAR, req);
  }

  /** Corrige un corte: sólo su fecha y su resultado. */
  actualizarSeguimiento(seguimiento: MatrizSeguimiento): Observable<MatrizRiesgoRes> {
    const req: MatrizRiesgoReq = { seguimiento };
    return this.http.post<MatrizRiesgoRes>(UrlConstantes.ISO42001_MATRIZ_SEG_ACTUALIZAR, MatrizRiesgoService.conFechas(req));
  }

  /** Los catalogos de criterios y la tabla de valoracion, en una llamada. */
  catalogos(): Observable<MatrizRiesgoRes> {
    return this.http.post<MatrizRiesgoRes>(UrlConstantes.ISO42001_MATRIZ_CATALOGOS, {});
  }

  /* ---------- Control de cambios: «Cambios por aprobar» ----------
   * Sin conFechas: las peticiones sólo llevan ids y un motivo. */

  /**
   * Las propuestas pendientes (de la más vieja a la más nueva) y las últimas
   * resueltas. Quien no tiene el perfil de edición sólo recibe lo que
   * cuentan el tablero y el libro (id, entidad, estatus y matriz), sin resueltas.
   */
  pendientes(limite?: number): Observable<MatrizPendienteRes> {
    const req: MatrizPendienteReq = { limite };
    return this.http.post<MatrizPendienteRes>(UrlConstantes.ISO42001_PEND_CONSULTAR, req);
  }

  /** Hoy en la matriz contra lo que se propone. Sólo lectura; pide el perfil. */
  detallePendiente(idPendiente: number): Observable<MatrizPendienteRes> {
    const req: MatrizPendienteReq = { idPendiente };
    return this.http.post<MatrizPendienteRes>(UrlConstantes.ISO42001_PEND_DETALLE, req);
  }

  /** La aplica de verdad. Devuelve en 'pendiente' la propuesta ya resuelta. */
  aceptarPendiente(idPendiente: number): Observable<MatrizPendienteRes> {
    const req: MatrizPendienteReq = { idPendiente };
    return this.http.post<MatrizPendienteRes>(UrlConstantes.ISO42001_PEND_ACEPTAR, req);
  }

  /** La tira sin tocar la matriz. El motivo es obligatorio. */
  descartarPendiente(idPendiente: number, motivo: string): Observable<MatrizPendienteRes> {
    const req: MatrizPendienteReq = { idPendiente, motivo };
    return this.http.post<MatrizPendienteRes>(UrlConstantes.ISO42001_PEND_DESCARTAR, req);
  }

  /* ---------- Riesgos de proceso (hoja Plan Gestión RSK) ---------- */

  /** Devuelve también los catálogos de severidad y las estrategias. */
  consultarRiesgos(filtro?: RiesgoProceso): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { filtro };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_RIESGOPROC_CONSULTAR, req);
  }

  /** El listado con el seguimiento de cada riesgo, en una sola llamada. */
  consultarRiesgosCompleto(filtro?: RiesgoProceso): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { filtro };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_RIESGOPROC_CONSULTAR_COMPLETO, req);
  }

  obtenerRiesgo(idRiesgoProceso: number): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { idRiesgoProceso };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_RIESGOPROC_OBTENER, req);
  }

  guardarRiesgo(riesgo: RiesgoProceso): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { riesgo };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_RIESGOPROC_GUARDAR, MatrizRiesgoService.conFechas(req));
  }

  actualizarRiesgo(riesgo: RiesgoProceso): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { riesgo };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_RIESGOPROC_ACTUALIZAR, MatrizRiesgoService.conFechas(req));
  }

  eliminarRiesgo(idRiesgoProceso: number): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { idRiesgoProceso };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_RIESGOPROC_ELIMINAR, req);
  }

  guardarSeguimientoRiesgo(seguimiento: RiesgoProcesoSeguimiento): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { seguimiento };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_RIESGOPROC_SEG_GUARDAR, MatrizRiesgoService.conFechas(req));
  }

  /**
   * Borra un corte. El resultado no se puede editar, así que corregir uno mal
   * capturado es quitarlo y volver a registrarlo.
   */
  eliminarSeguimientoRiesgo(idRpSeguimiento: number): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { idRpSeguimiento };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_RIESGOPROC_SEG_ELIMINAR, req);
  }

  /** Corrige un corte: sólo su fecha y su resultado. */
  actualizarSeguimientoRiesgo(seguimiento: RiesgoProcesoSeguimiento): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { seguimiento };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_RIESGOPROC_SEG_ACTUALIZAR, MatrizRiesgoService.conFechas(req));
  }

  /* ---------- Control de cambios del documento ---------- */

  versiones(idMatriz?: number): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { idMatriz: idMatriz || 1 };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_MATRIZ_VER_CONSULTAR, req);
  }

  guardarVersion(version: MatrizVersion): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { version };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_MATRIZ_VER_GUARDAR, MatrizRiesgoService.conFechas(req));
  }

  actualizarVersion(version: MatrizVersion): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { version };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_MATRIZ_VER_ACTUALIZAR, MatrizRiesgoService.conFechas(req));
  }

  eliminarVersion(idMatrizVersion: number): Observable<RiesgoProcesoRes> {
    const req: RiesgoProcesoReq = { version: { idMatrizVersion } };
    return this.http.post<RiesgoProcesoRes>(UrlConstantes.ISO42001_MATRIZ_VER_ELIMINAR, req);
  }

  /**
   * Descarga el libro completo. Va por HttpClient y no por window.open para
   * que el interceptor agregue el token: el endpoint valida la sesión.
   */
  exportarExcel(idMatriz?: number): Observable<Blob> {
    // Sin matriz baja la 1 (IA), que es lo que hacia el enlace de siempre.
    const url = UrlConstantes.ISO42001_MATRIZ_EXPORTAR + "?idMatriz=" + (idMatriz || 1);
    return this.http.get(url, { responseType: "blob" });
  }

  /* ---------- Gestión de Riesgos: las matrices por área ---------- */

  consultarMatrices(filtro?: Matriz): Observable<MatrizRes> {
    return this.http.post<MatrizRes>(UrlConstantes.ISO42001_GR_CONSULTAR, { filtro });
  }

  obtenerMatriz(idMatriz: number): Observable<MatrizRes> {
    return this.http.post<MatrizRes>(UrlConstantes.ISO42001_GR_OBTENER, { idMatriz });
  }

  /** Da de alta la matriz y le genera su menú dentro de Gestión Riesgos. */
  crearMatriz(matriz: Matriz, perfiles?: number[]): Observable<MatrizRes> {
    return this.http.post<MatrizRes>(UrlConstantes.ISO42001_GR_CREAR, { matriz, perfiles });
  }

  /* ---------- Carga del formato FOS-005 ----------
   * Dos pasos: la previa NO escribe nada y sirve para revisar; sólo al
   * confirmar se crea la matriz con todo su contenido. El archivo viaja las
   * dos veces a propósito, para no dejar estado a medias en el servidor. */

  private formaImportacion(archivo: File, m: Matriz, huella?: string): FormData {
    const f = new FormData();
    f.append("file", archivo, archivo.name);
    f.append("nombre", m.nombre || "");
    f.append("clave", m.clave || "");
    if (m.idCatArea) {
      f.append("idCatArea", String(m.idCatArea));
    }
    f.append("alcance", m.alcance || "");
    f.append("proceso", m.proceso || "");
    f.append("aplicaActivos", m.aplicaActivos || "NO");
    if (huella) {
      f.append("huella", huella);
    }
    return f;
  }

  /** Lee el archivo y devuelve lo que se crearía. No escribe nada. */
  previaImportacion(archivo: File, m: Matriz): Observable<ImportacionRes> {
    return this.http.post<ImportacionRes>(
      UrlConstantes.ISO42001_GR_PREVIA, this.formaImportacion(archivo, m));
  }

  /** Crea la matriz con todo el contenido del archivo, en una transacción. */
  importarMatriz(archivo: File, m: Matriz, huella?: string): Observable<ImportacionRes> {
    return this.http.post<ImportacionRes>(
      UrlConstantes.ISO42001_GR_IMPORTAR, this.formaImportacion(archivo, m, huella));
  }

  actualizarMatriz(matriz: Matriz): Observable<MatrizRes> {
    return this.http.post<MatrizRes>(UrlConstantes.ISO42001_GR_ACTUALIZAR, { matriz });
  }

  cambiarEstatusMatriz(idMatriz: number, estatus: string): Observable<MatrizRes> {
    return this.http.post<MatrizRes>(UrlConstantes.ISO42001_GR_ESTATUS, { idMatriz, estatus });
  }

  /* ---------- Avisos de monitoreo (29/09/2026, solo en pantalla) ---------- */

  /** Quiénes reciben los avisos de la matriz y quiénes los pueden recibir. */
  consultarAvisos(idMatriz: number): Observable<MatrizRes> {
    return this.http.post<MatrizRes>(UrlConstantes.ISO42001_GR_AVISOS, { idMatriz });
  }

  guardarAvisos(idMatriz: number, idsUsuario: number[]): Observable<MatrizRes> {
    return this.http.post<MatrizRes>(UrlConstantes.ISO42001_GR_AVISOS_GUARDAR, { idMatriz, idsUsuario });
  }

  /** Riesgos y activos de las matrices activas, con su último corte, y las mías. */
  consultarMonitoreos(): Observable<MatrizRes> {
    return this.http.post<MatrizRes>(UrlConstantes.ISO42001_GR_MONITOREOS, {});
  }

  eliminarMatriz(idMatriz: number): Observable<MatrizRes> {
    return this.http.post<MatrizRes>(UrlConstantes.ISO42001_GR_ELIMINAR, { idMatriz });
  }

  /**
   * Borra la matriz CON todo su contenido. Pide la contraseña del propio
   * usuario, y el backend valida además que su perfil lo permita.
   */
  eliminarMatrizCompleta(idMatriz: number, password: string): Observable<MatrizRes> {
    return this.http.post<MatrizRes>(
      UrlConstantes.ISO42001_GR_ELIMINAR_TODO, { idMatriz, password });
  }

  /* ---------- Evidencias adjuntas (activos y riesgos de proceso) ---------- */

  subirEvidencia(archivo: File, tipoOrigen: string, idOrigen: number,
                 descripcion?: string, idSeguimiento?: number | null): Observable<MatrizEvidenciaRes> {
    const forma = new FormData();
    forma.append("file", archivo, archivo.name);
    forma.append("tipoOrigen", tipoOrigen);
    forma.append("idOrigen", String(idOrigen));
    if (descripcion) {
      forma.append("descripcion", descripcion);
    }
    // El corte al que respalda, si se eligió uno.
    if (idSeguimiento) {
      forma.append("idSeguimiento", String(idSeguimiento));
    }
    return this.http.post<MatrizEvidenciaRes>(UrlConstantes.ISO42001_MATRIZ_EVIDENCIA_SUBIR, forma);
  }

  consultarEvidencias(tipoOrigen: string, idOrigen: number): Observable<MatrizEvidenciaRes> {
    return this.http.post<MatrizEvidenciaRes>(
      UrlConstantes.ISO42001_MATRIZ_EVIDENCIA_CONSULTAR, { tipoOrigen, idOrigen });
  }

  /**
   * Los bytes del archivo, por HttpClient para que viaje el token. El
   * componente decide: abrirlos en una pestaña (imagen/PDF) o guardarlos.
   */
  descargarEvidencia(idEvidencia: number): Observable<Blob> {
    return this.http.get(
      UrlConstantes.ISO42001_MATRIZ_EVIDENCIA_DESCARGAR + "?idEvidencia=" + idEvidencia,
      { responseType: "blob" });
  }

  eliminarEvidencia(idEvidencia: number): Observable<MatrizEvidenciaRes> {
    return this.http.post<MatrizEvidenciaRes>(
      UrlConstantes.ISO42001_MATRIZ_EVIDENCIA_ELIMINAR, { idEvidencia });
  }

  /** Declara si las evidencias aplican (SI/NO) para un activo o un riesgo. */
  marcarAplicaEvidencia(tipoOrigen: string, idOrigen: number,
                        aplica: string): Observable<MatrizEvidenciaRes> {
    return this.http.post<MatrizEvidenciaRes>(
      UrlConstantes.ISO42001_MATRIZ_EVIDENCIA_APLICA, { tipoOrigen, idOrigen, aplica });
  }
}
